import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Paper,
  Typography,
  Button,
  IconButton,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Divider,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Autocomplete,
  Snackbar,
  Alert,
  CircularProgress,
  Tooltip,
  Chip,
  Tabs,
  Tab,
  Drawer,
  Grid,
  Rating,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
} from '@mui/material';
import {
  Add,
  Refresh,
  MoreVert,
  WorkOutline,
  Groups,
  Assignment,
  Send,
  CheckCircle,
  CalendarToday,
  Close,
  Edit,
  Person,
  Email,
  Phone,
  EventAvailable,
} from '@mui/icons-material';
import {
  PageHeader,
  StatCard,
  FilterBar,
  FilterSpacer,
  TableSurface,
  headerCellSx,
  bodyRowSx,
  StatusDot,
  StatusTone,
  TypePill,
  ErrorState,
  TableEmptyState,
} from '../../components/common/pageKit';
import { formatIndianNumber, formatIndianDate } from '../../utils/currency';
import hrPlusService from '../../services/hrPlusService';
import hrService, { Department, EmployeeListItem } from '../../services/hrService';

// ---------------------------------------------------------------------------
// Types — shaped from erp-api's HrRecruitment model/controller (job_requisitions
// / job_applications / interviews / offers). Numeric DECIMAL columns come back
// as number | string (mysql2 default), so every money field is parsed via
// toNumber() before formatting — never rendered raw.
// ---------------------------------------------------------------------------

type PipelineStage = 'APPLIED' | 'SCREENING' | 'INTERVIEW' | 'OFFER' | 'HIRED' | 'REJECTED';
const PIPELINE_STAGES: PipelineStage[] = ['APPLIED', 'SCREENING', 'INTERVIEW', 'OFFER', 'HIRED', 'REJECTED'];

type RequisitionStatus = 'DRAFT' | 'SUBMITTED' | 'APPROVED' | 'REJECTED' | 'FULFILLED' | 'CLOSED';
type InterviewStatus = 'SCHEDULED' | 'COMPLETED' | 'CANCELLED' | 'NO_SHOW';
type OfferStatus = 'DRAFT' | 'SENT' | 'ACCEPTED' | 'DECLINED' | 'EXPIRED' | 'REVOKED';

interface PipelineCandidate {
  id: number;
  candidate_name: string;
  email: string | null;
  phone: string | null;
  stage: PipelineStage;
  rating: number | null;
  current_employer: string | null;
  current_ctc: number | string | null;
  expected_ctc: number | string | null;
  notice_period: string | null;
  experience_years: number | string | null;
  source: string | null;
  skills: string | null;
  converted_employee_id: number | null;
  created_at: string;
  job_opening_id: number;
  job_title: string;
  job_code: string;
}

interface InterviewRecord {
  id: number;
  application_id: number;
  round: string | null;
  interviewer: string | null;
  scheduled_at: string | null;
  mode: string | null;
  status: InterviewStatus;
  rating: number | null;
  feedback: string | null;
  recommendation: string | null;
  created_at: string;
}

interface OfferRecord {
  id: number;
  application_id: number;
  candidate_name: string;
  position: string | null;
  offered_ctc: number | string | null;
  joining_date: string | null;
  status: OfferStatus;
  approved_by: number | null;
  created_at: string;
}

interface ApplicationDetail extends PipelineCandidate {
  resume_url: string | null;
  updated_at: string;
  department_id: number | null;
  interviews: InterviewRecord[];
  offer: OfferRecord | null;
  timeline: Array<{ event: string; round?: string | null; at: string }>;
}

interface Requisition {
  id: number;
  requisition_code: string;
  department_id: number | null;
  position: string;
  openings: number;
  grade: string | null;
  location: string | null;
  hiring_manager_id: number | null;
  budget_ctc: number | string | null;
  justification: string | null;
  status: RequisitionStatus;
  approved_by: number | null;
  job_opening_id: number | null;
  created_by: number | null;
  created_at: string;
  dept_name: string | null;
  hiring_manager_name: string | null;
  job_opening_code: string | null;
}

interface RecruitmentAnalytics {
  funnel: Record<string, number>;
  offersByStatus: Record<string, number>;
  offerAcceptanceRate: number;
  avgTimeToHireDays: number | null;
  bySource: Array<{ source: string; count: number }>;
  openRequisitions: { count: number; totalOpenings: number };
}

// ---------------------------------------------------------------------------
// Presentation constants — stage identity colour, deliberately its own
// palette so a viewer never conflates "which column" with "good/bad".
// ---------------------------------------------------------------------------

const stageTints: Record<PipelineStage, string> = {
  APPLIED: '#64748b',
  SCREENING: '#0284c7',
  INTERVIEW: '#7c3aed',
  OFFER: '#d97706',
  HIRED: '#059669',
  REJECTED: '#dc2626',
};

const stageLabels: Record<PipelineStage, string> = {
  APPLIED: 'Applied',
  SCREENING: 'Screening',
  INTERVIEW: 'Interview',
  OFFER: 'Offer',
  HIRED: 'Hired',
  REJECTED: 'Rejected',
};

const requisitionStatusTone: Record<RequisitionStatus, StatusTone> = {
  DRAFT: 'neutral',
  SUBMITTED: 'info',
  APPROVED: 'success',
  REJECTED: 'error',
  FULFILLED: 'success',
  CLOSED: 'neutral',
};

const offerStatusTone: Record<OfferStatus, StatusTone> = {
  DRAFT: 'neutral',
  SENT: 'info',
  ACCEPTED: 'success',
  DECLINED: 'error',
  EXPIRED: 'warning',
  REVOKED: 'error',
};

const interviewStatusTone: Record<InterviewStatus, StatusTone> = {
  SCHEDULED: 'info',
  COMPLETED: 'success',
  CANCELLED: 'neutral',
  NO_SHOW: 'warning',
};

const REQUISITION_STATUSES: RequisitionStatus[] = ['DRAFT', 'SUBMITTED', 'APPROVED', 'REJECTED', 'FULFILLED', 'CLOSED'];
const OFFER_STATUSES: OfferStatus[] = ['DRAFT', 'SENT', 'ACCEPTED', 'DECLINED', 'EXPIRED', 'REVOKED'];
const INTERVIEW_STATUSES: InterviewStatus[] = ['SCHEDULED', 'COMPLETED', 'CANCELLED', 'NO_SHOW'];

/** Next valid moves for a requisition, shown as a "Move to" action menu. */
const nextRequisitionActions = (status: RequisitionStatus): { label: string; status: RequisitionStatus }[] => {
  switch (status) {
    case 'DRAFT':
      return [{ label: 'Submit for Approval', status: 'SUBMITTED' }];
    case 'SUBMITTED':
      return [
        { label: 'Approve', status: 'APPROVED' },
        { label: 'Reject', status: 'REJECTED' },
      ];
    case 'APPROVED':
      return [
        { label: 'Mark Fulfilled', status: 'FULFILLED' },
        { label: 'Close', status: 'CLOSED' },
      ];
    case 'FULFILLED':
      return [{ label: 'Close', status: 'CLOSED' }];
    default:
      return [];
  }
};

const toNumber = (v: number | string | null | undefined): number | null => {
  if (v === null || v === undefined || v === '') return null;
  const n = typeof v === 'string' ? parseFloat(v) : v;
  return Number.isFinite(n) ? n : null;
};

const money = (v: number | string | null | undefined): string => {
  const n = toNumber(v);
  return n === null ? '—' : formatIndianNumber(n);
};

const fullName = (e: { first_name: string; last_name: string | null }): string =>
  [e.first_name, e.last_name].filter(Boolean).join(' ');

/** Small stable palette for candidate source chips — cycles through a fixed
 * set of tints keyed by string hash so the same source always gets the same
 * colour, without maintaining an exhaustive source->colour map. */
const sourceTints = ['#0284c7', '#7c3aed', '#059669', '#d97706', '#db2777', '#0891b2'];
const sourceTint = (source: string | null): string => {
  if (!source) return '#94a3b8';
  let hash = 0;
  for (let i = 0; i < source.length; i++) hash = (hash * 31 + source.charCodeAt(i)) >>> 0;
  return sourceTints[hash % sourceTints.length];
};

type TabKey = 'pipeline' | 'requisitions';

const RecruitmentAtsPage: React.FC = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState<TabKey>('pipeline');

  const [toast, setToast] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  // ---- Analytics (KPI row — shared by both tabs) ----
  const [analytics, setAnalytics] = useState<RecruitmentAnalytics | null>(null);
  const [analyticsError, setAnalyticsError] = useState<string | null>(null);

  const fetchAnalytics = useCallback(async () => {
    try {
      const data = await hrPlusService.recruitmentAnalytics();
      setAnalytics(data as RecruitmentAnalytics);
      setAnalyticsError(null);
    } catch (err: any) {
      setAnalyticsError(err.message || 'Failed to load recruitment analytics');
    }
  }, []);

  // ---- Pipeline (Kanban) ----
  const [candidates, setCandidates] = useState<PipelineCandidate[]>([]);
  const [pipelineLoading, setPipelineLoading] = useState(true);
  const [pipelineError, setPipelineError] = useState<string | null>(null);
  const [savingIds, setSavingIds] = useState<Set<number>>(new Set());
  const [dragOverStage, setDragOverStage] = useState<PipelineStage | null>(null);
  const [draggingId, setDraggingId] = useState<number | null>(null);
  const [cardMenu, setCardMenu] = useState<{ anchor: HTMLElement; candidate: PipelineCandidate } | null>(null);

  const fetchPipeline = useCallback(async () => {
    setPipelineLoading(true);
    setPipelineError(null);
    try {
      const res = await hrPlusService.pipeline();
      const flat: PipelineCandidate[] = (res.stages || []).flatMap((s: any) => s.candidates || []);
      setCandidates(flat);
    } catch (err: any) {
      setPipelineError(err.message || 'Failed to load the recruitment pipeline');
    } finally {
      setPipelineLoading(false);
    }
  }, []);

  /** Re-syncs the board without flashing the full-page loading state — used
   * after a drawer action (schedule interview, create offer, convert…) that
   * may have moved a candidate's stage server-side. */
  const syncPipelineQuiet = useCallback(async () => {
    try {
      const res = await hrPlusService.pipeline();
      const flat: PipelineCandidate[] = (res.stages || []).flatMap((s: any) => s.candidates || []);
      setCandidates(flat);
    } catch {
      // Non-fatal — the board just keeps its last known state.
    }
  }, []);

  const columns = useMemo(
    () => PIPELINE_STAGES.map((stage) => ({ stage, deals: candidates.filter((c) => c.stage === stage) })),
    [candidates]
  );

  const moveCandidate = useCallback(
    async (id: number, newStage: PipelineStage) => {
      const previous = candidates;
      const target = previous.find((c) => c.id === id);
      if (!target || target.stage === newStage) return;

      setCandidates((prev) => prev.map((c) => (c.id === id ? { ...c, stage: newStage } : c)));
      setSavingIds((prev) => new Set(prev).add(id));

      try {
        await hrPlusService.moveApplicationStage(id, newStage);
      } catch (err: any) {
        setCandidates(previous);
        setToast({ message: err.message || `Could not move "${target.candidate_name}" — reverted.`, severity: 'error' });
      } finally {
        setSavingIds((prev) => {
          const next = new Set(prev);
          next.delete(id);
          return next;
        });
      }
    },
    [candidates]
  );

  const handleDragStart = (e: React.DragEvent, id: number) => {
    setDraggingId(id);
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(id));
  };
  const handleDragEnd = () => {
    setDraggingId(null);
    setDragOverStage(null);
  };
  const handleColumnDragOver = (e: React.DragEvent, stage: PipelineStage) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverStage !== stage) setDragOverStage(stage);
  };
  const handleColumnDrop = (e: React.DragEvent, stage: PipelineStage) => {
    e.preventDefault();
    const idStr = e.dataTransfer.getData('text/plain');
    const id = idStr ? parseInt(idStr, 10) : draggingId;
    setDragOverStage(null);
    setDraggingId(null);
    if (id !== null && !Number.isNaN(id)) moveCandidate(id, stage);
  };

  // ---- Requisitions ----
  const [requisitions, setRequisitions] = useState<Requisition[]>([]);
  const [reqLoading, setReqLoading] = useState(true);
  const [reqError, setReqError] = useState<string | null>(null);
  const [reqMenu, setReqMenu] = useState<{ anchor: HTMLElement; req: Requisition } | null>(null);

  const fetchRequisitions = useCallback(async () => {
    setReqLoading(true);
    setReqError(null);
    try {
      const res = await hrPlusService.getRequisitions();
      setRequisitions(res.requisitions || []);
    } catch (err: any) {
      setReqError(err.message || 'Failed to load job requisitions');
    } finally {
      setReqLoading(false);
    }
  }, []);

  const handleRequisitionStatus = async (req: Requisition, status: RequisitionStatus) => {
    setReqMenu(null);
    try {
      await hrPlusService.setRequisitionStatus(req.id, status);
      setToast({ message: `${req.requisition_code} moved to ${status}`, severity: 'success' });
      await fetchRequisitions();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update requisition status', severity: 'error' });
    }
  };

  // New requisition dialog
  const [reqDialogOpen, setReqDialogOpen] = useState(false);
  const [pickersLoaded, setPickersLoaded] = useState(false);
  const [departments, setDepartments] = useState<Department[]>([]);
  const [managers, setManagers] = useState<EmployeeListItem[]>([]);
  const emptyReqForm = {
    position: '',
    departmentId: null as number | null,
    openings: 1,
    grade: '',
    location: '',
    hiringManagerId: null as number | null,
    budgetCtc: '',
    justification: '',
  };
  const [reqForm, setReqForm] = useState(emptyReqForm);
  const [reqSaving, setReqSaving] = useState(false);

  const openReqDialog = async () => {
    setReqForm(emptyReqForm);
    setReqDialogOpen(true);
    if (!pickersLoaded) {
      try {
        const [deptRes, empRes] = await Promise.all([hrService.getDepartments(), hrService.getEmployees({ limit: 200 })]);
        setDepartments(deptRes.departments || []);
        setManagers(empRes.employees || []);
        setPickersLoaded(true);
      } catch {
        // Non-fatal — the pickers just stay empty; position is still enterable.
      }
    }
  };

  const handleCreateRequisition = async () => {
    if (!reqForm.position.trim()) return;
    setReqSaving(true);
    try {
      await hrPlusService.createRequisition({
        position: reqForm.position.trim(),
        departmentId: reqForm.departmentId ?? undefined,
        openings: reqForm.openings || 1,
        grade: reqForm.grade.trim() || undefined,
        location: reqForm.location.trim() || undefined,
        hiringManagerId: reqForm.hiringManagerId ?? undefined,
        budgetCtc: reqForm.budgetCtc ? parseFloat(reqForm.budgetCtc) : undefined,
        justification: reqForm.justification.trim() || undefined,
      });
      setToast({ message: 'Job requisition created', severity: 'success' });
      setReqDialogOpen(false);
      await Promise.all([fetchRequisitions(), fetchAnalytics()]);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to create requisition', severity: 'error' });
    } finally {
      setReqSaving(false);
    }
  };

  // ---- Initial load ----
  useEffect(() => {
    fetchPipeline();
    fetchRequisitions();
    fetchAnalytics();
  }, [fetchPipeline, fetchRequisitions, fetchAnalytics]);

  // -------------------------------------------------------------------------
  // Candidate detail drawer
  // -------------------------------------------------------------------------
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [detail, setDetail] = useState<ApplicationDetail | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailError, setDetailError] = useState<string | null>(null);

  const loadDetail = useCallback(async (id: number) => {
    setDetailLoading(true);
    setDetailError(null);
    try {
      const res = await hrPlusService.getApplication(id);
      setDetail(res.application as ApplicationDetail);
    } catch (err: any) {
      setDetailError(err.message || 'Failed to load candidate details');
    } finally {
      setDetailLoading(false);
    }
  }, []);

  const openCandidate = (id: number) => {
    setSelectedId(id);
    setDetail(null);
    setDrawerOpen(true);
    loadDetail(id);
  };

  const closeDrawer = () => {
    setDrawerOpen(false);
    setSelectedId(null);
    setDetail(null);
  };

  const refreshDetail = useCallback(async () => {
    if (selectedId !== null) await loadDetail(selectedId);
  }, [selectedId, loadDetail]);

  // ---- Enrichment form ----
  const emptyEnrichForm = {
    currentEmployer: '',
    currentCtc: '',
    expectedCtc: '',
    noticePeriod: '',
    experienceYears: '',
    source: '',
    skills: '',
  };
  const [enrichForm, setEnrichForm] = useState(emptyEnrichForm);
  const [enrichRating, setEnrichRating] = useState<number | null>(null);
  const [enrichSaving, setEnrichSaving] = useState(false);

  // Offer creation form
  const [offerForm, setOfferForm] = useState({ offeredCtc: '', joiningDate: '', position: '' });
  const [offerSaving, setOfferSaving] = useState(false);
  const [offerStatusSaving, setOfferStatusSaving] = useState(false);

  // Interview scheduling form
  const [scheduleOpen, setScheduleOpen] = useState(false);
  const [scheduleForm, setScheduleForm] = useState({ round: '', interviewer: '', scheduledAt: '', mode: '' });
  const [scheduleSaving, setScheduleSaving] = useState(false);

  // Interview status/feedback edit dialog
  const [interviewEdit, setInterviewEdit] = useState<InterviewRecord | null>(null);
  const [interviewEditForm, setInterviewEditForm] = useState({
    status: 'SCHEDULED' as InterviewStatus,
    rating: '',
    feedback: '',
    recommendation: '',
  });
  const [interviewSaving, setInterviewSaving] = useState(false);

  const [converting, setConverting] = useState(false);

  useEffect(() => {
    if (!detail) return;
    setEnrichForm({
      currentEmployer: detail.current_employer || '',
      currentCtc: detail.current_ctc != null ? String(detail.current_ctc) : '',
      expectedCtc: detail.expected_ctc != null ? String(detail.expected_ctc) : '',
      noticePeriod: detail.notice_period || '',
      experienceYears: detail.experience_years != null ? String(detail.experience_years) : '',
      source: detail.source || '',
      skills: detail.skills || '',
    });
    setEnrichRating(detail.rating ?? null);
    setOfferForm((f) => ({ ...f, position: f.position || detail.job_title || '' }));
  }, [detail]);

  const handleSaveEnrichment = async () => {
    if (selectedId === null) return;
    setEnrichSaving(true);
    try {
      const payload: Record<string, any> = {
        currentEmployer: enrichForm.currentEmployer || undefined,
        noticePeriod: enrichForm.noticePeriod || undefined,
        source: enrichForm.source || undefined,
        skills: enrichForm.skills || undefined,
      };
      const currentCtc = toNumber(enrichForm.currentCtc);
      if (currentCtc !== null) payload.currentCtc = currentCtc;
      const expectedCtc = toNumber(enrichForm.expectedCtc);
      if (expectedCtc !== null) payload.expectedCtc = expectedCtc;
      const experienceYears = toNumber(enrichForm.experienceYears);
      if (experienceYears !== null) payload.experienceYears = experienceYears;
      if (enrichRating !== null) payload.rating = enrichRating;

      await hrPlusService.updateApplication(selectedId, payload);
      setToast({ message: 'Candidate details updated', severity: 'success' });
      await refreshDetail();
      await syncPipelineQuiet();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update candidate details', severity: 'error' });
    } finally {
      setEnrichSaving(false);
    }
  };

  const handleScheduleInterview = async () => {
    if (selectedId === null) return;
    setScheduleSaving(true);
    try {
      await hrPlusService.scheduleInterview(selectedId, {
        round: scheduleForm.round || undefined,
        interviewer: scheduleForm.interviewer || undefined,
        scheduledAt: scheduleForm.scheduledAt || undefined,
        mode: scheduleForm.mode || undefined,
      });
      setToast({ message: 'Interview scheduled', severity: 'success' });
      setScheduleOpen(false);
      setScheduleForm({ round: '', interviewer: '', scheduledAt: '', mode: '' });
      await refreshDetail();
      await syncPipelineQuiet();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to schedule interview', severity: 'error' });
    } finally {
      setScheduleSaving(false);
    }
  };

  const openInterviewEdit = (iv: InterviewRecord) => {
    setInterviewEdit(iv);
    setInterviewEditForm({
      status: iv.status,
      rating: iv.rating != null ? String(iv.rating) : '',
      feedback: iv.feedback || '',
      recommendation: iv.recommendation || '',
    });
  };

  const handleSaveInterview = async () => {
    if (!interviewEdit) return;
    setInterviewSaving(true);
    try {
      const rating = toNumber(interviewEditForm.rating);
      await hrPlusService.updateInterview(interviewEdit.id, {
        status: interviewEditForm.status,
        rating: rating !== null ? rating : undefined,
        feedback: interviewEditForm.feedback || undefined,
        recommendation: interviewEditForm.recommendation || undefined,
      });
      setToast({ message: 'Interview updated', severity: 'success' });
      setInterviewEdit(null);
      await refreshDetail();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update interview', severity: 'error' });
    } finally {
      setInterviewSaving(false);
    }
  };

  const handleCreateOffer = async () => {
    if (selectedId === null) return;
    const offeredCtc = toNumber(offerForm.offeredCtc);
    if (offeredCtc === null) {
      setToast({ message: 'Offered CTC is required', severity: 'error' });
      return;
    }
    setOfferSaving(true);
    try {
      await hrPlusService.createOffer(selectedId, {
        offeredCtc,
        joiningDate: offerForm.joiningDate || undefined,
        position: offerForm.position || undefined,
      });
      setToast({ message: 'Offer created', severity: 'success' });
      await refreshDetail();
      await syncPipelineQuiet();
      await fetchAnalytics();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to create offer', severity: 'error' });
    } finally {
      setOfferSaving(false);
    }
  };

  const handleOfferStatus = async (status: OfferStatus) => {
    if (!detail?.offer) return;
    setOfferStatusSaving(true);
    try {
      await hrPlusService.setOfferStatus(detail.offer.id, status);
      setToast({ message: `Offer status set to ${status}`, severity: 'success' });
      await refreshDetail();
      await syncPipelineQuiet();
      await fetchAnalytics();
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to update offer status', severity: 'error' });
    } finally {
      setOfferStatusSaving(false);
    }
  };

  const handleConvert = async () => {
    if (!detail) return;
    if (detail.converted_employee_id) {
      navigate(`/hr/employees/${detail.converted_employee_id}/360`);
      return;
    }
    setConverting(true);
    try {
      const res = await hrPlusService.convertCandidate(detail.id);
      setToast({ message: `${detail.candidate_name} converted to employee`, severity: 'success' });
      await syncPipelineQuiet();
      closeDrawer();
      navigate(`/hr/employees/${res.employee.id}/360`);
    } catch (err: any) {
      setToast({ message: err.message || 'Failed to convert candidate', severity: 'error' });
    } finally {
      setConverting(false);
    }
  };

  const pipelineActiveCount = analytics
    ? (analytics.funnel.APPLIED || 0) + (analytics.funnel.SCREENING || 0) + (analytics.funnel.INTERVIEW || 0) + (analytics.funnel.OFFER || 0)
    : null;

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minHeight: 0 }}>
      <PageHeader
        title="Recruitment ATS"
        subtitle="Requisitions, candidate pipeline, interviews, offers and hiring"
        actions={
          <>
            <Tooltip title="Refresh">
              <IconButton
                onClick={() => {
                  fetchPipeline();
                  fetchRequisitions();
                  fetchAnalytics();
                }}
                disabled={pipelineLoading || reqLoading}
              >
                <Refresh />
              </IconButton>
            </Tooltip>
            <Button variant="contained" startIcon={<Add />} size="small" onClick={openReqDialog}>
              New Requisition
            </Button>
          </>
        }
      />

      {analyticsError && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setAnalyticsError(null)}>
          {analyticsError}
        </Alert>
      )}

      <Grid container spacing={2} sx={{ mb: 2.5 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Open Requisitions"
            value={analytics ? analytics.openRequisitions.count : null}
            icon={<Assignment />}
            color="#0284c7"
            footer={
              analytics ? (
                <Typography variant="caption" color="text.secondary">
                  {analytics.openRequisitions.totalOpenings} openings
                </Typography>
              ) : undefined
            }
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard title="Candidates in Pipeline" value={pipelineActiveCount} icon={<Groups />} color="#7c3aed" />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Offers Out"
            value={analytics ? analytics.offersByStatus.SENT ?? 0 : null}
            icon={<Send />}
            color="#d97706"
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <StatCard
            title="Offer Acceptance Rate"
            value={analytics ? `${analytics.offerAcceptanceRate}%` : null}
            icon={<CheckCircle />}
            color="#059669"
          />
        </Grid>
      </Grid>

      <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2.5, minHeight: 40 }}>
        <Tab label="Pipeline" value="pipeline" sx={{ minHeight: 40 }} />
        <Tab label="Requisitions" value="requisitions" sx={{ minHeight: 40 }} />
      </Tabs>

      {tab === 'pipeline' ? (
        pipelineLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
            <CircularProgress />
          </Box>
        ) : pipelineError ? (
          <ErrorState message={pipelineError} onRetry={fetchPipeline} />
        ) : candidates.length === 0 ? (
          <TableEmptyState title="No candidates yet" hint="Applications will appear here once requisitions attract candidates." />
        ) : (
          <Box
            sx={{
              display: 'flex',
              gap: 2,
              overflowX: 'auto',
              overflowY: 'hidden',
              pb: 1,
              flexGrow: 1,
              minHeight: 0,
            }}
          >
            {columns.map(({ stage, deals }) => {
              const tint = stageTints[stage];
              const isDragOver = dragOverStage === stage;
              return (
                <Box
                  key={stage}
                  role="region"
                  aria-label={`${stageLabels[stage]} — ${deals.length} candidates`}
                  onDragOver={(e) => handleColumnDragOver(e, stage)}
                  onDragLeave={() => setDragOverStage((s) => (s === stage ? null : s))}
                  onDrop={(e) => handleColumnDrop(e, stage)}
                  sx={{
                    display: 'flex',
                    flexDirection: 'column',
                    width: 288,
                    flexShrink: 0,
                    bgcolor: isDragOver ? `${tint}0f` : 'transparent',
                    borderRadius: 3,
                    transition: 'background-color .15s ease',
                  }}
                >
                  <Paper
                    elevation={0}
                    sx={{
                      p: 1.5,
                      mb: 1.25,
                      borderRadius: 2.5,
                      border: '1px solid',
                      borderColor: isDragOver ? tint : 'divider',
                      borderTop: '3px solid',
                      borderTopColor: tint,
                      transition: 'border-color .15s ease',
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                      <TypePill label={stageLabels[stage]} tint={tint} />
                      <Typography variant="caption" sx={{ fontWeight: 700, color: 'text.secondary', fontVariantNumeric: 'tabular-nums' }}>
                        {deals.length}
                      </Typography>
                    </Box>
                  </Paper>

                  <Box sx={{ flexGrow: 1, overflowY: 'auto', minHeight: 120, display: 'flex', flexDirection: 'column', gap: 1, pb: 1, pr: 0.5 }}>
                    {deals.length === 0 ? (
                      <Box sx={{ border: '1px dashed', borderColor: 'divider', borderRadius: 2, py: 3, textAlign: 'center' }}>
                        <Typography variant="caption" color="text.disabled">
                          No candidates
                        </Typography>
                      </Box>
                    ) : (
                      deals.map((candidate) => {
                        const isSaving = savingIds.has(candidate.id);
                        const isDragging = draggingId === candidate.id;
                        return (
                          <Paper
                            key={candidate.id}
                            elevation={0}
                            draggable
                            onDragStart={(e) => handleDragStart(e, candidate.id)}
                            onDragEnd={handleDragEnd}
                            tabIndex={0}
                            role="article"
                            aria-roledescription="Draggable candidate card"
                            aria-label={`${candidate.candidate_name}, ${candidate.job_title}, stage ${stageLabels[stage]}`}
                            onClick={() => openCandidate(candidate.id)}
                            sx={{
                              p: 1.5,
                              borderRadius: 2,
                              border: '1px solid',
                              borderColor: 'divider',
                              cursor: 'grab',
                              opacity: isDragging ? 0.5 : isSaving ? 0.7 : 1,
                              transition: 'opacity .15s ease, box-shadow .15s ease',
                              '&:hover': { boxShadow: 2 },
                              '&:focus-visible': { outline: `2px solid ${tint}`, outlineOffset: 1 },
                            }}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 0.5 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem', lineHeight: 1.3 }}>{candidate.candidate_name}</Typography>
                              <IconButton
                                size="small"
                                aria-label={`Change stage or view ${candidate.candidate_name}`}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setCardMenu({ anchor: e.currentTarget, candidate });
                                }}
                                sx={{ mt: -0.5, mr: -0.5, flexShrink: 0 }}
                              >
                                <MoreVert fontSize="small" />
                              </IconButton>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.5 }}>
                              <WorkOutline sx={{ fontSize: 13, color: 'text.disabled' }} />
                              <Typography variant="caption" color="text.secondary" noWrap>
                                {candidate.job_title}
                              </Typography>
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.75 }}>
                              <Rating value={candidate.rating || 0} readOnly size="small" max={5} />
                              {candidate.rating != null && (
                                <Typography variant="caption" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                  {candidate.rating}/5
                                </Typography>
                              )}
                            </Box>

                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                              <Typography
                                title={candidate.expected_ctc != null ? String(candidate.expected_ctc) : undefined}
                                sx={{ fontWeight: 700, fontSize: '0.8125rem', fontVariantNumeric: 'tabular-nums' }}
                              >
                                {money(candidate.expected_ctc)}
                              </Typography>
                              {candidate.source && <TypePill label={candidate.source} tint={sourceTint(candidate.source)} />}
                            </Box>

                            {isSaving && (
                              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 0.5 }}>
                                <CircularProgress size={10} />
                              </Box>
                            )}
                          </Paper>
                        );
                      })
                    )}
                  </Box>
                </Box>
              );
            })}
          </Box>
        )
      ) : reqLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 6 }}>
          <CircularProgress />
        </Box>
      ) : reqError ? (
        <ErrorState message={reqError} onRetry={fetchRequisitions} />
      ) : (
        <TableSurface>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell sx={headerCellSx}>Position</TableCell>
                  <TableCell sx={headerCellSx}>Department</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Openings
                  </TableCell>
                  <TableCell sx={headerCellSx}>Grade</TableCell>
                  <TableCell sx={headerCellSx}>Location</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Budget CTC
                  </TableCell>
                  <TableCell sx={headerCellSx}>Status</TableCell>
                  <TableCell sx={headerCellSx} align="right">
                    Actions
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {requisitions.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8}>
                      <TableEmptyState title="No requisitions yet" hint="Create one to open a new pipeline." />
                    </TableCell>
                  </TableRow>
                ) : (
                  requisitions.map((req) => (
                    <TableRow key={req.id} sx={bodyRowSx}>
                      <TableCell>
                        <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>{req.position}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {req.requisition_code}
                        </Typography>
                      </TableCell>
                      <TableCell>{req.dept_name || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {req.openings}
                      </TableCell>
                      <TableCell>{req.grade || '—'}</TableCell>
                      <TableCell>{req.location || '—'}</TableCell>
                      <TableCell align="right" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {money(req.budget_ctc)}
                      </TableCell>
                      <TableCell>
                        <StatusDot label={req.status} tone={requisitionStatusTone[req.status]} />
                      </TableCell>
                      <TableCell align="right">
                        {nextRequisitionActions(req.status).length > 0 && (
                          <IconButton size="small" onClick={(e) => setReqMenu({ anchor: e.currentTarget, req })}>
                            <MoreVert fontSize="small" />
                          </IconButton>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </TableSurface>
      )}

      {/* ---- Card "Move to" menu — keyboard-reachable stage change ---- */}
      <Menu anchorEl={cardMenu?.anchor || null} open={!!cardMenu} onClose={() => setCardMenu(null)}>
        <Typography variant="caption" sx={{ px: 2, py: 0.5, display: 'block', color: 'text.secondary' }}>
          Move to
        </Typography>
        {PIPELINE_STAGES.filter((s) => s !== cardMenu?.candidate.stage).map((s) => (
          <MenuItem
            key={s}
            onClick={() => {
              if (cardMenu) moveCandidate(cardMenu.candidate.id, s);
              setCardMenu(null);
            }}
          >
            <ListItemIcon>
              <Box sx={{ width: 10, height: 10, borderRadius: '50%', backgroundColor: stageTints[s] }} />
            </ListItemIcon>
            <ListItemText>{stageLabels[s]}</ListItemText>
          </MenuItem>
        ))}
        <Divider />
        <MenuItem
          onClick={() => {
            if (cardMenu) openCandidate(cardMenu.candidate.id);
            setCardMenu(null);
          }}
        >
          <ListItemIcon>
            <Person fontSize="small" />
          </ListItemIcon>
          <ListItemText>View candidate</ListItemText>
        </MenuItem>
      </Menu>

      {/* ---- Requisition status "Move to" menu ---- */}
      <Menu anchorEl={reqMenu?.anchor || null} open={!!reqMenu} onClose={() => setReqMenu(null)}>
        {reqMenu &&
          nextRequisitionActions(reqMenu.req.status).map((action) => (
            <MenuItem key={action.status} onClick={() => handleRequisitionStatus(reqMenu.req, action.status)}>
              {action.label}
            </MenuItem>
          ))}
      </Menu>

      {/* ---- New requisition dialog ---- */}
      <Dialog open={reqDialogOpen} onClose={() => setReqDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Job Requisition</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Position"
              value={reqForm.position}
              onChange={(e) => setReqForm((f) => ({ ...f, position: e.target.value }))}
              required
            />
            <Autocomplete
              options={departments}
              getOptionLabel={(o) => o.dept_name || ''}
              value={departments.find((d) => d.id === reqForm.departmentId) || null}
              onChange={(_, v) => setReqForm((f) => ({ ...f, departmentId: v?.id ?? null }))}
              renderInput={(params) => <TextField {...params} label="Department" />}
            />
            <Autocomplete
              options={managers}
              getOptionLabel={(o) => `${fullName(o)} (${o.employee_code})`}
              value={managers.find((m) => m.id === reqForm.hiringManagerId) || null}
              onChange={(_, v) => setReqForm((f) => ({ ...f, hiringManagerId: v?.id ?? null }))}
              renderInput={(params) => <TextField {...params} label="Hiring Manager" />}
            />
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Openings"
                type="number"
                inputProps={{ min: 1 }}
                value={reqForm.openings}
                onChange={(e) => setReqForm((f) => ({ ...f, openings: Math.max(1, parseInt(e.target.value, 10) || 1) }))}
              />
              <TextField fullWidth label="Grade" value={reqForm.grade} onChange={(e) => setReqForm((f) => ({ ...f, grade: e.target.value }))} />
            </Box>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <TextField
                fullWidth
                label="Location"
                value={reqForm.location}
                onChange={(e) => setReqForm((f) => ({ ...f, location: e.target.value }))}
              />
              <TextField
                fullWidth
                label="Budget CTC"
                type="number"
                value={reqForm.budgetCtc}
                onChange={(e) => setReqForm((f) => ({ ...f, budgetCtc: e.target.value }))}
              />
            </Box>
            <TextField
              fullWidth
              multiline
              minRows={2}
              label="Justification"
              value={reqForm.justification}
              onChange={(e) => setReqForm((f) => ({ ...f, justification: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setReqDialogOpen(false)} disabled={reqSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleCreateRequisition} disabled={reqSaving || !reqForm.position.trim()}>
            {reqSaving ? 'Saving…' : 'Create Requisition'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Candidate detail drawer ---- */}
      <Drawer anchor="right" open={drawerOpen} onClose={closeDrawer}>
        <Box sx={{ width: { xs: '100vw', sm: 460 }, display: 'flex', flexDirection: 'column', height: '100%' }}>
          <Box sx={{ p: 2.5, borderBottom: '1px solid', borderColor: 'divider', display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="h6" sx={{ fontWeight: 700 }} noWrap>
                {detail?.candidate_name || '—'}
              </Typography>
              {detail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                  <TypePill label={stageLabels[detail.stage]} tint={stageTints[detail.stage]} />
                  <Typography variant="caption" color="text.secondary">
                    {detail.job_title} ({detail.job_code})
                  </Typography>
                </Box>
              )}
            </Box>
            <IconButton onClick={closeDrawer} size="small">
              <Close fontSize="small" />
            </IconButton>
          </Box>

          <Box sx={{ flexGrow: 1, overflowY: 'auto', p: 2.5 }}>
            {detailLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
                <CircularProgress size={28} />
              </Box>
            ) : detailError ? (
              <ErrorState message={detailError} onRetry={refreshDetail} />
            ) : detail ? (
              <Stack spacing={3}>
                {/* Contact */}
                <Box>
                  <Stack spacing={0.5}>
                    {detail.email && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Email sx={{ fontSize: 15, color: 'text.disabled' }} />
                        <Typography variant="body2">{detail.email}</Typography>
                      </Box>
                    )}
                    {detail.phone && (
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                        <Phone sx={{ fontSize: 15, color: 'text.disabled' }} />
                        <Typography variant="body2">{detail.phone}</Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <CalendarToday sx={{ fontSize: 15, color: 'text.disabled' }} />
                      <Typography variant="caption" color="text.secondary">
                        Applied {formatIndianDate(new Date(detail.created_at))}
                      </Typography>
                    </Box>
                  </Stack>
                </Box>

                <Divider />

                {/* Enrichment form */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Candidate Details
                  </Typography>
                  <Stack spacing={1.5}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
                        Rating
                      </Typography>
                      <Rating value={enrichRating} onChange={(_, v) => setEnrichRating(v)} />
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Current Employer"
                      value={enrichForm.currentEmployer}
                      onChange={(e) => setEnrichForm((f) => ({ ...f, currentEmployer: e.target.value }))}
                    />
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Current CTC"
                        type="number"
                        value={enrichForm.currentCtc}
                        onChange={(e) => setEnrichForm((f) => ({ ...f, currentCtc: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Expected CTC"
                        type="number"
                        value={enrichForm.expectedCtc}
                        onChange={(e) => setEnrichForm((f) => ({ ...f, expectedCtc: e.target.value }))}
                      />
                    </Box>
                    <Box sx={{ display: 'flex', gap: 1.5 }}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Notice Period"
                        value={enrichForm.noticePeriod}
                        onChange={(e) => setEnrichForm((f) => ({ ...f, noticePeriod: e.target.value }))}
                      />
                      <TextField
                        fullWidth
                        size="small"
                        label="Experience (yrs)"
                        type="number"
                        value={enrichForm.experienceYears}
                        onChange={(e) => setEnrichForm((f) => ({ ...f, experienceYears: e.target.value }))}
                      />
                    </Box>
                    <TextField
                      fullWidth
                      size="small"
                      label="Source"
                      value={enrichForm.source}
                      onChange={(e) => setEnrichForm((f) => ({ ...f, source: e.target.value }))}
                    />
                    <TextField
                      fullWidth
                      size="small"
                      multiline
                      minRows={2}
                      label="Skills"
                      value={enrichForm.skills}
                      onChange={(e) => setEnrichForm((f) => ({ ...f, skills: e.target.value }))}
                    />
                    <Button variant="outlined" size="small" onClick={handleSaveEnrichment} disabled={enrichSaving} sx={{ alignSelf: 'flex-start' }}>
                      {enrichSaving ? 'Saving…' : 'Save Changes'}
                    </Button>
                  </Stack>
                </Box>

                <Divider />

                {/* Interviews */}
                <Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1.5 }}>
                    <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
                      Interviews
                    </Typography>
                    <Button size="small" startIcon={<Add />} onClick={() => setScheduleOpen(true)}>
                      Schedule
                    </Button>
                  </Box>
                  {detail.interviews.length === 0 ? (
                    <Typography variant="caption" color="text.disabled">
                      No interviews scheduled yet.
                    </Typography>
                  ) : (
                    <Stack spacing={1}>
                      {detail.interviews.map((iv) => (
                        <Paper key={iv.id} elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                          <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
                            <Box sx={{ minWidth: 0 }}>
                              <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>{iv.round || 'Interview'}</Typography>
                              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                {iv.interviewer || 'Unassigned'} · {iv.mode || '—'}
                              </Typography>
                              {iv.scheduled_at && (
                                <Typography variant="caption" color="text.secondary" sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.25 }}>
                                  <EventAvailable sx={{ fontSize: 13 }} />
                                  {formatIndianDate(new Date(iv.scheduled_at))}
                                </Typography>
                              )}
                            </Box>
                            <IconButton size="small" onClick={() => openInterviewEdit(iv)}>
                              <Edit fontSize="small" />
                            </IconButton>
                          </Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 1 }}>
                            <StatusDot label={iv.status} tone={interviewStatusTone[iv.status]} />
                            {iv.rating != null && (
                              <Typography variant="caption" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                                {iv.rating}/5
                              </Typography>
                            )}
                          </Box>
                          {iv.feedback && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                              {iv.feedback}
                            </Typography>
                          )}
                        </Paper>
                      ))}
                    </Stack>
                  )}
                </Box>

                <Divider />

                {/* Offer */}
                <Box>
                  <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5 }}>
                    Offer
                  </Typography>
                  {detail.offer ? (
                    <Paper elevation={0} sx={{ p: 1.5, borderRadius: 2, border: '1px solid', borderColor: 'divider' }}>
                      <Typography sx={{ fontWeight: 700, fontSize: '0.8125rem' }}>{detail.offer.position || detail.job_title}</Typography>
                      <Typography sx={{ fontWeight: 700, fontVariantNumeric: 'tabular-nums', mt: 0.5 }}>{money(detail.offer.offered_ctc)}</Typography>
                      {detail.offer.joining_date && (
                        <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                          Joining {formatIndianDate(new Date(detail.offer.joining_date))}
                        </Typography>
                      )}
                      <Box sx={{ mt: 1.25 }}>
                        <TextField
                          select
                          size="small"
                          fullWidth
                          label="Offer Status"
                          value={detail.offer.status}
                          onChange={(e) => handleOfferStatus(e.target.value as OfferStatus)}
                          disabled={offerStatusSaving}
                        >
                          {OFFER_STATUSES.map((s) => (
                            <MenuItem key={s} value={s}>
                              {s}
                            </MenuItem>
                          ))}
                        </TextField>
                      </Box>
                    </Paper>
                  ) : (
                    <Stack spacing={1.5}>
                      <TextField
                        fullWidth
                        size="small"
                        label="Position"
                        value={offerForm.position}
                        onChange={(e) => setOfferForm((f) => ({ ...f, position: e.target.value }))}
                      />
                      <Box sx={{ display: 'flex', gap: 1.5 }}>
                        <TextField
                          fullWidth
                          size="small"
                          label="Offered CTC"
                          type="number"
                          required
                          value={offerForm.offeredCtc}
                          onChange={(e) => setOfferForm((f) => ({ ...f, offeredCtc: e.target.value }))}
                        />
                        <TextField
                          fullWidth
                          size="small"
                          label="Joining Date"
                          type="date"
                          InputLabelProps={{ shrink: true }}
                          value={offerForm.joiningDate}
                          onChange={(e) => setOfferForm((f) => ({ ...f, joiningDate: e.target.value }))}
                        />
                      </Box>
                      <Button variant="outlined" size="small" onClick={handleCreateOffer} disabled={offerSaving} sx={{ alignSelf: 'flex-start' }}>
                        {offerSaving ? 'Creating…' : 'Create Offer'}
                      </Button>
                    </Stack>
                  )}
                </Box>

                {(detail.stage === 'OFFER' || detail.stage === 'HIRED') && (
                  <>
                    <Divider />
                    <Button variant="contained" color="success" onClick={handleConvert} disabled={converting}>
                      {converting ? 'Converting…' : detail.converted_employee_id ? 'View Employee Record' : 'Convert to Employee'}
                    </Button>
                  </>
                )}
              </Stack>
            ) : null}
          </Box>
        </Box>
      </Drawer>

      {/* ---- Schedule interview dialog ---- */}
      <Dialog open={scheduleOpen} onClose={() => setScheduleOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Schedule Interview</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              fullWidth
              label="Round"
              value={scheduleForm.round}
              onChange={(e) => setScheduleForm((f) => ({ ...f, round: e.target.value }))}
              placeholder="e.g. Technical Round 1"
            />
            <TextField
              fullWidth
              label="Interviewer"
              value={scheduleForm.interviewer}
              onChange={(e) => setScheduleForm((f) => ({ ...f, interviewer: e.target.value }))}
            />
            <TextField
              fullWidth
              label="Scheduled At"
              type="datetime-local"
              InputLabelProps={{ shrink: true }}
              value={scheduleForm.scheduledAt}
              onChange={(e) => setScheduleForm((f) => ({ ...f, scheduledAt: e.target.value }))}
            />
            <TextField
              fullWidth
              select
              label="Mode"
              value={scheduleForm.mode}
              onChange={(e) => setScheduleForm((f) => ({ ...f, mode: e.target.value }))}
            >
              <MenuItem value="">Unspecified</MenuItem>
              <MenuItem value="IN_PERSON">In Person</MenuItem>
              <MenuItem value="VIDEO">Video</MenuItem>
              <MenuItem value="PHONE">Phone</MenuItem>
            </TextField>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setScheduleOpen(false)} disabled={scheduleSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleScheduleInterview} disabled={scheduleSaving}>
            {scheduleSaving ? 'Scheduling…' : 'Schedule'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ---- Update interview dialog ---- */}
      <Dialog open={!!interviewEdit} onClose={() => setInterviewEdit(null)} maxWidth="xs" fullWidth>
        <DialogTitle>Update Interview</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              select
              fullWidth
              label="Status"
              value={interviewEditForm.status}
              onChange={(e) => setInterviewEditForm((f) => ({ ...f, status: e.target.value as InterviewStatus }))}
            >
              {INTERVIEW_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </TextField>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary" sx={{ minWidth: 56 }}>
                Rating
              </Typography>
              <Rating
                value={toNumber(interviewEditForm.rating)}
                onChange={(_, v) => setInterviewEditForm((f) => ({ ...f, rating: v != null ? String(v) : '' }))}
              />
            </Box>
            <TextField
              select
              fullWidth
              label="Recommendation"
              value={interviewEditForm.recommendation}
              onChange={(e) => setInterviewEditForm((f) => ({ ...f, recommendation: e.target.value }))}
            >
              <MenuItem value="">Unspecified</MenuItem>
              <MenuItem value="STRONG_YES">Strong Yes</MenuItem>
              <MenuItem value="YES">Yes</MenuItem>
              <MenuItem value="NO">No</MenuItem>
              <MenuItem value="STRONG_NO">Strong No</MenuItem>
            </TextField>
            <TextField
              fullWidth
              multiline
              minRows={3}
              label="Feedback"
              value={interviewEditForm.feedback}
              onChange={(e) => setInterviewEditForm((f) => ({ ...f, feedback: e.target.value }))}
            />
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2.5 }}>
          <Button onClick={() => setInterviewEdit(null)} disabled={interviewSaving}>
            Cancel
          </Button>
          <Button variant="contained" onClick={handleSaveInterview} disabled={interviewSaving}>
            {interviewSaving ? 'Saving…' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={!!toast} autoHideDuration={5000} onClose={() => setToast(null)} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
        {toast ? (
          <Alert severity={toast.severity} onClose={() => setToast(null)}>
            {toast.message}
          </Alert>
        ) : undefined}
      </Snackbar>
    </Box>
  );
};

export default RecruitmentAtsPage;
